import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import ProgressIndicator from 'components/common/progress-indicator';
import CheckboxInput from 'components/inputs/checkbox';
import NumberInput from 'components/inputs/number';
import NumberPresetInput from 'components/inputs/number-preset';
import SelectInput from 'components/inputs/select';
import TextInput from 'components/inputs/text';
import TextareaInput from 'components/inputs/textarea';
import ToggleSwitch from 'components/inputs/toggle-switch';
import InfoTooltip from 'components/layout/info-tooltip';
import { AttributeType } from 'data/types';
import { ReactNode } from 'react';
import { isEmpty } from 'utils/helpers';

interface AttributeComponentProps extends AttributeType {
  showProgress?: boolean;
  showLabel?: boolean;
  completed?: boolean;
  onChange?: (value: any) => void;
  debounce?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  width?: number;
  preventDeselection?: boolean;
  tooltipLocation?: 'default' | 'after_label';
  inlineLabel?: boolean;
  presets?: number[];
}

export default function AttributeComponent({
  showProgress = true,
  showLabel = true,
  completed,
  id,
  type,
  label,
  code,
  priority,
  inheritable,
  parentAttributes,
  required,
  group,
  advanced,
  modified,
  width,
  preventDeselection,
  tooltipLocation = 'default',
  tooltip,
  inlineLabel = false,
  presets,
  ...props
}: AttributeComponentProps) {
  const developerMode = useAppSelector((state) => state.ui.developerMode);
  const codeTitle = developerMode ? code : undefined;

  let inputElement: ReactNode | null = null;

  const enableChips =
    (props.options?.length ?? 0) <= 3 &&
    (props.options?.every((o) =>
      typeof o === 'string' ? o.length <= 25 : o.value.length <= 25
    ) ??
      false);

  const hasPresets = (presets?.length ?? 0) > 0;

  const inlineInputFillsWidth =
    inlineLabel &&
    type !== 'toggleSwitch' &&
    !(type === 'select' && enableChips) &&
    !(type === 'number' && hasPresets);

  if (type === 'number') {
    inputElement =
      presets && presets.length > 0 ? (
        <NumberPresetInput
          {...props}
          presets={presets}
          invalid={required && !showProgress && isEmpty(props.value)}
        />
      ) : (
        <NumberInput
          {...props}
          invalid={required && !showProgress && isEmpty(props.value)}
        />
      );
  }

  if (type === 'select') {
    inputElement = (
      <SelectInput
        {...props}
        enableChips={enableChips}
        invalid={required && !showProgress && isEmpty(props.value)}
        preventDeselection={preventDeselection}
      />
    );
  }

  if (type === 'text') {
    inputElement = (
      <TextInput
        {...props}
        invalid={required && !showProgress && isEmpty(props.value)}
      />
    );
  }

  if (type === 'textarea') {
    inputElement = <TextareaInput {...props} />;
  }

  if (type === 'toggleSwitch') {
    inputElement = <ToggleSwitch {...props} />;
  }

  if (type === 'checkbox') {
    return (
      <label
        className={`flex items-center gap-2 ${
          props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
        title={codeTitle}
        data-attribute-code={code}
      >
        <CheckboxInput {...props} />
        {showLabel && (
          <span className='font-semibold text-gray-800'>{label}</span>
        )}
        {tooltip && <InfoTooltip>{tooltip}</InfoTooltip>}
      </label>
    );
  }

  return (
    <div
      className={
        inlineLabel ? 'flex items-center gap-3' : 'flex flex-col gap-2'
      }
      title={codeTitle}
      data-attribute-code={code}
    >
      {(showProgress || (showLabel && label)) && (
        <div className='flex items-center gap-2'>
          {showProgress && (
            <ProgressIndicator required={required} checked={completed} />
          )}
          {showLabel && (
            <span className='font-semibold text-gray-800'>{label}</span>
          )}
          {tooltip && tooltipLocation === 'after_label' && (
            <InfoTooltip>{tooltip}</InfoTooltip>
          )}
        </div>
      )}
      <div
        className={clsx(
          'flex items-center gap-2',
          inlineInputFillsWidth && 'w-44'
        )}
      >
        {width && (
          <div style={{ width }}>
            {inputElement ?? 'Invalid attribute type!'}
          </div>
        )}
        {!width && (inputElement ?? 'Invalid attribute type!')}
        {tooltip && tooltipLocation === 'default' && (
          <InfoTooltip>{tooltip}</InfoTooltip>
        )}
      </div>
    </div>
  );
}
